import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import * as signalR from "@microsoft/signalr";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { yardRepairsTableColumns } from "../../../data/FormColumns/Fleet/yardRepairsTableColumns";

import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchYardRepairs = async (
  token: string | null | undefined,
  month?: number,
  year?: number,
) => {
  const { data } = await axios.get(`${apiVariable}/yardRepair`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      month: month || undefined,
      year: year || undefined,
    },
  });
  return data;
};

const YardRepairsTable: React.FC = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1,
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["yardRepairs", selectedMonth, selectedYear, showAll],
    queryFn: () =>
      fetchYardRepairs(
        token,
        showAll ? undefined : selectedMonth,
        showAll ? undefined : selectedYear,
      ),
  });

  useEffect(() => {
    const yardRepairConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/yardRepairHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    yardRepairConnection.on("YardRepairUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["yardRepairs"] });
    });

    yardRepairConnection.start();

    return () => {
      yardRepairConnection.stop();
    };
  }, [token, queryClient]);

  const filteredYardRepairs = useMemo(() => {
    if (!data) return [];
    return data.filter((repair: any) =>
      [repair.unitNumber, repair.invoiceDate, repair.invoiceNumber].some(
        (field) =>
          field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  return (
    <>
      <Box className="incidents-table-container" height={900}>
        <Box
          className="table-controls"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: 2,
          }}
        >
          <Grid container spacing={2} sx={{ width: "auto" }}>
            <Grid item>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  disabled={showAll}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  disabled={showAll}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAll}
                    onChange={(e) => {
                      setShowAll(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedMonth(new Date().getMonth() + 1);
                        setSelectedYear(new Date().getFullYear());
                      }
                    }}
                  />
                }
                label="Show All Yard Repairs"
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              position: "relative",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Unit, Invoice date, Invoice number"
              sx={{ maxWidth: "100%", width: 400 }}
            />
          </Box>
        </Box>

        {isLoading ? (
          <Box className="table-loader">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box className="table-error">
            <Typography color="error">
              Error: {(error as any).message}
            </Typography>
          </Box>
        ) : (
          <PersistedDataGrid
            tableName="yardRepairs"
            rows={filteredYardRepairs}
            columns={yardRepairsTableColumns}
            compactViewConfig={{
              primaryField: "unitNumber",
              secondaryFields: ["driverFullName", "invoiceDate"],
              detailFields: [
                "description",
                "amount",
                "invoiceNumber",
                "authorizedBy",
                "mileage",
                "rentCompanyName",
                "ownerCompanyName",
                "isCompanyCharged",
                "chargedCompanyAmount",
                "isDriverCharged",
                "chargedDriverAmount",
              ],
              actionFields: ["downloadYardRepairInvoice", "update", "delete"],
            }}
            sx={{ height: 700 }}
          />
        )}
      </Box>
    </>
  );
};

export default YardRepairsTable;
